/**
 * Helpers - вспомогательные функции
 */

/**
 * Debounce
 */
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Throttle
 */
export const throttle = (func, limit) => {
    let inThrottle;
    return function executedFunction(...args) {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};

/**
 * Форматирование даты
 */
export const formatDate = (date, options = {}) => {
    const d = new Date(date);
    return d.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        ...options,
    });
};

/**
 * Форматирование времени
 */
export const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

/**
 * Форматирование числа с разделителями
 */
export const formatNumber = (num) => {
    return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

/**
 * Склонение слов
 */
export const declension = (number, titles) => {
    const cases = [2, 0, 1, 1, 1, 2];
    return titles[
        number % 100 > 4 && number % 100 < 20
            ? 2
            : cases[number % 10 < 5 ? number % 10 : 5]
    ];
};

/**
 * Генерация случайной строки
 */
export const randomString = (length = 8) => {
    return Math.random()
        .toString(36)
        .substring(2, 2 + length);
};

/**
 * Копирование в буфер обмена
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        return false;
    }
};

/**
 * Проверка на мобильное устройство
 */
export const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
    );
};

/**
 * Прокрутка к элементу
 */
export const scrollToElement = (elementId, offset = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
        const top =
            element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: "smooth" });
    }
};

/**
 * Парсинг JWT токена
 */
export const parseJWT = (token) => {
    try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map(
                    (c) =>
                        "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2),
                )
                .join(""),
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

const PORTRAIT_RACE_SLUG = {
    1: "human",
    2: "elf",
    4: "dwarf",
    6: "nimf",
};

export const getCharacterPortraitSrc = (characterInfo) => {
    if (!characterInfo || typeof characterInfo !== "object") {
        return "/media/shield_kadia_02.png";
    }
    const slug =
        characterInfo.portraitRaceSlug ||
        PORTRAIT_RACE_SLUG[characterInfo.gender] ||
        "human";
    const sex = characterInfo.sex === 0 ? "male" : "female";
    const skin = characterInfo.selectedSkin ?? 2;
    return `/media/${slug}_${sex}_${skin}.png`;
};

const SHOP_IMAGE_FALLBACK = "/media/shield_kadia_02.png";

export const getShopImageCandidates = (imgUrl) => {
    if (!imgUrl) return [SHOP_IMAGE_FALLBACK];

    let raw = String(imgUrl).trim();

    if (/^https?:\/\//i.test(raw)) {
        try {
            raw = new URL(raw).pathname || "";
        } catch {
            return [SHOP_IMAGE_FALLBACK];
        }
    }

    const normalizedPath = raw
        .replace(/\\/g, "/")
        .replace(/^\/+/, "")
        .replace(/^media\//i, "");

    if (!normalizedPath) return [SHOP_IMAGE_FALLBACK];

    return [`/media/${normalizedPath}`, SHOP_IMAGE_FALLBACK];
};

export default {
    debounce,
    throttle,
    formatDate,
    formatTime,
    formatNumber,
    declension,
    randomString,
    copyToClipboard,
    isMobile,
    scrollToElement,
    parseJWT,
    getCharacterPortraitSrc,
    getShopImageCandidates,
};
